// transactionPipelineCells — inline cell components for the pipeline surface.
//
// Split out of `transactionPipelineFields.tsx` so that module exports only
// field constants and no components (react-refresh/only-export-components).
// Consumed exclusively by the field constants in that file.

import React from "react";
import { Tooltip } from "antd";
import { CopyOutlined, EditOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import type { Transaction, TransactionFile, Transport } from "@shared/types/transaction";
import type { DocumentType, PriceType } from "@shared/types";

import { EntityLink } from "@ds/connected/EntityLink";
import { TextValue } from "@ds/display/TextValue";
import { EmptyValue } from "@ds/display/EmptyValue";
import { AmountDisplay } from "@ds/display/AmountDisplay";
import { Dot } from "@ds/display/Dot";
import { Inline } from "@ds/layout/Inline";
import { Box } from "@ds/layout/Box";
import { HoverShell } from "@ds/editable/HoverShell";
import { IconButton } from "@ds/primitives/IconButton";
import { TONE } from "@ds/tokens/palette";
import { useFormat } from "@ds/display/useNumberFormat";
import { formatPriceSuffix } from "@shared/calc";
import type { TransactionActionsContextValue } from "./hooks/TransactionActionsContext";
import { TransactionDocsBucket } from "./cells/TransactionDocsBucket";

export type TxExtras = TransactionActionsContextValue | null | undefined;

// ─── Inline cell components ───────────────────────────────────────────────────

export function DaysSinceInlineCell({
  date, redThreshold, amberThreshold,
}: {
  date: string | null;
  redThreshold: { gt?: number; gte?: number };
  amberThreshold: { gt?: number; gte?: number };
}): React.ReactElement {
  const fmt = useFormat();
  if (!date) return <EmptyValue />;
  const days = fmt.daysSince(date);
  if (days === null) return <EmptyValue />;
  const exceeds = (t: { gt?: number; gte?: number }) =>
    (t.gt  !== undefined && days >  t.gt) ||
    (t.gte !== undefined && days >= t.gte);
  const toneName = exceeds(redThreshold) ? "red"
                 : exceeds(amberThreshold) ? "amber"
                 : undefined;
  return <TextValue value={days} weight="semibold" size="md" tone={toneName} />;
}

function PriceSourceDot({ source }: { source: "price_list" | "manual" }): React.ReactElement {
  const { t } = useTranslation();
  const label = source === "price_list"
    ? t("transactions.pipeline.price_source_price_list")
    : t("transactions.pipeline.price_source_manual");
  return (
    <Tooltip title={label}>
      <Inline gap="none" align="center" inline>
        <Dot tone={source === "price_list" ? "teal" : "neutral"} size="xs" />
      </Inline>
    </Tooltip>
  );
}

export function SidePriceInlineCell({
  isHidden, price, currency, priceType, priceSource, side, transactionId, extras,
}: {
  isHidden: boolean;
  price: number | null;
  currency: string;
  priceType: PriceType;
  priceSource: "price_list" | "manual";
  side: "load" | "unload";
  transactionId: number;
  extras: TxExtras;
}): React.ReactElement | null {
  if (isHidden) return null;
  const body = price != null
    ? <AmountDisplay value={price} currency={currency} colored signedNeutral
        size="sm" strong suffix={formatPriceSuffix(priceType)} />
    : <TextValue tone="faint" italic value="Edytuj cenę" />;
  return (
    <Inline gap="xs" align="center">
      {price != null && <PriceSourceDot source={priceSource} />}
      {extras ? (
        <HoverShell affordance="edit" onClick={() => extras.actions.setPrice(transactionId, side)}>
          {body}
        </HoverShell>
      ) : body}
    </Inline>
  );
}

export function RefInlineCell({
  record, extras,
}: {
  record: Transaction;
  extras: TxExtras;
}): React.ReactElement {
  const { t } = useTranslation();
  if (!extras) {
    return (
      <EntityLink
        type="transaction"
        id={record.id}
        label={record.reference_number ?? `#${record.id}`}
        variant="inline"
      />
    );
  }
  const editLabel = t("transactions.pipeline.ref_edit");
  const duplicateLabel = t("transactions.pipeline.ref_duplicate");
  return (
    <Box>
      <Inline gap="xs" align="center">
        <EntityLink
          type="transaction"
          id={record.id}
          label={record.reference_number}
          variant="inline"
        />
        {record.status_override === "cancelled" && (
          <Tooltip title="Anulowane">
            <ExclamationCircleFilled style={{ color: TONE.red.base }} />
          </Tooltip>
        )}
        <IconButton
          variant="ghost"
          size="xs"
          icon={<EditOutlined />}
          ariaLabel={editLabel}
          testId={`row-action-edit-${record.id}`}
          onClick={(e) => { e.stopPropagation(); extras.actions.editTx(record.id); }}
        />
        <IconButton
          variant="ghost"
          size="xs"
          icon={<CopyOutlined />}
          ariaLabel={duplicateLabel}
          testId={`row-action-duplicate-${record.id}`}
          onClick={(e) => { e.stopPropagation(); extras.actions.duplicateTx(record.id); }}
        />
      </Inline>
      {record.external_id && (
        <TextValue value={record.external_id} size="xs" tone="muted" lineHeight="tight" as="div" />
      )}
    </Box>
  );
}

// DocsInlineCell / PhotosInlineCell / GeneratedDocsInlineCell — thin wrappers
// over the shared `<TransactionDocsBucket>` so cells and the entity-view
// documents section render identically. Hover preview, regenerate, photo
// status grouping all live in that component.
//
// `isHidden` (legacy gate on a `*_check === "not_required"`) is enforced here
// before delegating — keeps the bucket component policy-free.

// DocsInlineCell / PhotosInlineCell / GeneratedDocsInlineCell — thin wrappers
// over the shared `<TransactionDocsBucket>`. The bucket reads workflow handlers
// from `useTransactionWorkflowOptional` internally, so the cells don't need
// to thread extras any more — they just supply data + a few flags.

export function DocsInlineCell({
  files, isHidden, checkPending, uploadStatus, useCustomsTypePicker,
  transactionId, referenceNumber,
}: {
  files: TransactionFile[];
  isHidden: boolean;
  checkPending: boolean;
  uploadStatus?: "copy" | "original";
  useCustomsTypePicker?: boolean;
  transactionId: number;
  referenceNumber?: string;
}): React.ReactElement | null {
  if (isHidden) return null;
  return (
    <TransactionDocsBucket
      mode="uploaded"
      layout="stack"
      files={files}
      transactionId={transactionId}
      checkPending={checkPending}
      uploadStatus={uploadStatus}
      referenceNumber={referenceNumber}
      useCustomsTypePicker={useCustomsTypePicker}
    />
  );
}

export function PhotosInlineCell({
  files, isHidden, checkPending, transactionId,
}: {
  files: TransactionFile[];
  isHidden: boolean;
  checkPending: boolean;
  transactionId: number;
}): React.ReactElement | null {
  if (isHidden) return null;
  return (
    <TransactionDocsBucket
      mode="photos"
      layout="stack"
      files={files}
      transactionId={transactionId}
      checkPending={checkPending}
    />
  );
}

export function GeneratedDocsInlineCell({
  files, transports, defs, transportDone, transactionId,
}: {
  files: TransactionFile[];
  transports: Transport[];
  defs: ReadonlyArray<{ type: DocumentType; label: string; checkStatus: string | null }>;
  transportDone: boolean;
  transactionId: number;
}): React.ReactElement {
  return (
    <TransactionDocsBucket
      mode="generated"
      layout="stack"
      files={files}
      transactionId={transactionId}
      defs={defs}
      transports={transports}
      transportDone={transportDone}
    />
  );
}

function TransportPriceRow({ tp }: { tp: Transport }): React.ReactElement {
  const fmt = useFormat();
  if (tp.price == null) return <EmptyValue />;
  const toneName = tp.price < 0 ? "red" : "green";
  const formatted = tp.price_currency
    ? fmt.currency(Math.abs(tp.price), tp.price_currency)
    : String(Math.abs(tp.price));
  return (
    <TextValue tone={toneName} weight="medium" tabular as="span">
      {tp.price < 0 ? `−${formatted}` : formatted}
      <TextValue tone={toneName} size="xs" as="span">
        {formatPriceSuffix(tp.price_type)}
      </TextValue>
    </TextValue>
  );
}

export function TransportPriceInlineCell({ transports }: { transports: Transport[] }): React.ReactElement {
  return (
    <>
      {transports.map((tp) => (
        <Box key={tp.id}><TransportPriceRow tp={tp} /></Box>
      ))}
    </>
  );
}
